import { useParams } from "react-router-dom";
import {
  useCompileCodeLazyQuery,
  useGetAssignmentQuery,
  useGetAssignmentQuestionForStudentsQuery,
  useGetAssignmentSubmitQuery,
  useGetCampusQuery,
  useGetStudentByEmailIdQuery,
  useSubmitAssignmentCodeMutation,
} from "../../../graphql/generated.tsx";
import React, { useState, useEffect, useRef } from "react";
import { toast } from "../../../toast";
import "codemirror/mode/clike/clike";
import "codemirror/mode/python/python";
import Compiler from "../../Practice/Components/Compiler";
import CustomInput from "../../Practice/Components/CustomInput";
import RunModel from "../../Practice/Components/RunModel";
import ErrorModel from "../../Practice/Components/ErrorModel";
import { Button, Modal } from "flowbite-react";
import TestLayout from "../Layout/TestLayout.jsx";
import CompilerQuestionCard from "../Components/CompilerQuestionCard.jsx";
import BackButton from "../Components/BackButton.jsx";
import SumbitModel from "../../Practice/Components/SumbitModel";
import Timer from "../Components/Timer.jsx";
const AssignmentTestQuestion = () => {
  // ...existing code...
  // Ref for autosubmit
  const submitRef = useRef();
  // Get params first
  const { questionId, assignmentId } = useParams();
  // Tab switch tracking: count per-assignment and autosubmit threshold
  const tabSwitchLimit = 2;
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  // Single-use guard to ensure we auto-submit only once when threshold reached
  const autoSubmittedRef = useRef(false);
  const [code, setCode] = useState("");
  const [codeApplied, setCodeApplied] = useState(false);
  const [output, setOutput] = useState("");
  const [error, setError] = useState("");
  const [input, setInput] = useState("");
  const [question, setQuestion] = useState({});
  const [isChecked, setIsChecked] = useState(false);
  const errorRef = useRef(null);
  const [testResults, setTestResults] = useState([]);
  const [run, setRun] = useState(false);
  const [submit, setSubmit] = useState(false);
  const tableRef = useRef(null);
  const [isFullScreen, setFullScreen] = useState(true);
  const [isCodeChanged, setIsCodeChanged] = useState(false);
  const [initialCode, setInitialCode] = useState("");
  const [showTabWarningModal, setShowTabWarningModal] = useState(false);
  const [tabSwitchReason, setTabSwitchReason] = useState("");
  const [showExitFullscreenModal, setShowExitFullscreenModal] = useState(false);
  const keyboardLockActive = useRef(false);
  const intentionalExitRef = useRef(false);
  const wakeLockRef = useRef(null);
  const timeAutoSubmitTriggeredRef = useRef(false);

  const { data, loading } = useGetAssignmentQuestionForStudentsQuery({
    variables: { id: questionId },
    fetchPolicy: "network-only",
  });

  const studentEmail = JSON.parse(localStorage.getItem("user")).email;

  const { data: student } = useGetStudentByEmailIdQuery({
    variables: { email: studentEmail },
    fetchPolicy: "network-only",
  });
  console.log("Student", student);

  // Load tab switch count from sessionStorage once student data is available
  useEffect(() => {
    if (student?.getStudentByEmailId?.id && assignmentId) {
      const sid = student.getStudentByEmailId.id;
      const storageKey = `tabSwitchCount_${sid}_${assignmentId}`;
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const loadedCount = parseInt(stored, 10);
        // ✅ Only load if it seems valid (< limit), otherwise reset
        if (loadedCount < tabSwitchLimit) {
          setTabSwitchCount(loadedCount);
          console.log(
            `Loaded tab switch count: ${loadedCount} from key: ${storageKey}`
          );
        } else {
          // ✅ Clear if at/above limit (stale data from previous failed attempt)
          sessionStorage.removeItem(storageKey);
          console.log(
            `Cleared stale tab switch count (${loadedCount}) from key: ${storageKey}`
          );
        }
      }
    }
  }, [student?.getStudentByEmailId?.id, assignmentId]);

  const { data: campus } = useGetCampusQuery({
    variables: { id: student?.getStudentByEmailId?.campusId },
    skip: !student?.getStudentByEmailId,
    fetchPolicy: "network-only",
  });

  const { data: PreviousSubmissions, refetch } = useGetAssignmentSubmitQuery({
    variables: {
      assignmentId,
      studentId: student?.getStudentByEmailId?.id,
      questionId,
      campus_name: campus?.campus?.name,
    },
    skip: !student?.getStudentByEmailId,
    fetchPolicy: "network-only",
  });

  const { data: Assignment } = useGetAssignmentQuery({
    variables: { id: assignmentId },
    fetchPolicy: "network-only",
  });

  const [language, setLanguage] = useState("");

  const [
    compileCode,
    { data: compileData, loading: compileLoading, error: compileerror },
  ] = useCompileCodeLazyQuery();

  const [submitCode, { data: submitData, loading: submitLoading }] =
    useSubmitAssignmentCodeMutation();

  const extractFunctionCode = (code, language, targetFunctionName) => {
    let functionRegex;

    const lang = language?.toLowerCase() || "";

    switch (lang) {
      case "python":
        functionRegex = new RegExp(
          `def\\s+${targetFunctionName}\\s*\\([^)]*\\)\\s*:[\\s\\S]*?(?=\\n\\S|$)`,
          "g"
        );
        break;
      case "java":
        functionRegex = new RegExp(
          `public\\s+static\\s+\\w+\\s+${targetFunctionName}\\s*\\([^)]*\\)\\s*{[^}]*}`,
          "g"
        );
        break;
      case "cpp":
      case "c":
        functionRegex = new RegExp(
          `\\w+(\\s*\\*\\s*|\\s+)${targetFunctionName}\\s*\\([^)]*\\)\\s*{([\\s\\S]*?)}(?![^}]*})`,
          "g"
        );
        break;
      default:
        return "";
    }

    const match = code?.match(functionRegex);
    return match ? match[0] : "";
  };

  const [autosavedCode, setAutosavedCode] = useState(null);

  const setDefaultCodeBasedOnLanguage = (langKey) => {
    const latestCode = selectEitherAutoSavedOrSubmittedCode(
      autosavedCode,
      null
    );

    const lang = latestCode?.language;
    if (latestCode && langKey && lang && langKey === lang) {
      if (data?.getQuestion?.showFunctionOnly) {
        const functionCode = extractFunctionCode(
          latestCode.answer,
          lang,
          data.getAssignmentQuestionForStudents.targetFunctionName
        );
        setCode(functionCode);
      } else {
        setCode(latestCode.answer);
        setInitialCode(latestCode.answer);
      }
      return;
    }

    const language = langKey?.toLowerCase();

    if (
      data?.getAssignmentQuestionForStudents?.showFunctionOnly &&
      data?.getAssignmentQuestionForStudents.predefinedCode[language]
    ) {
      const functionCode = extractFunctionCode(
        data?.getAssignmentQuestionForStudents.predefinedCode[language],
        language,
        data?.getAssignmentQuestionForStudents.targetFunctionName
      );
      setCode(functionCode);
      setInitialCode(functionCode);
    } else if (
      data?.getAssignmentQuestionForStudents?.predefinedCode?.[language]
    ) {
      setCode(data?.getAssignmentQuestionForStudents?.predefinedCode[language]);
      setInitialCode(
        data?.getAssignmentQuestionForStudents.predefinedCode[language]
      );
    } else {
      const defaultTemplate =
        language === "python"
          ? "#Type your code here"
          : "//Type your code here";
      setCode(defaultTemplate);
      setInitialCode(defaultTemplate);
    }
  };

  const selectEitherAutoSavedOrSubmittedCode = (
    autosavedCode,
    latestSubmissions
  ) => {
    const validAutoSave = autosavedCode?.answer && autosavedCode?.language;

    if (!latestSubmissions?.length && !validAutoSave) return null;

    let latestObj = null;

    if (validAutoSave && latestSubmissions?.length) {
      const savedTime = new Date(autosavedCode.submittedAt);
      const latestSubmissionTime = new Date(latestSubmissions?.[0].submittedAt);
      latestObj =
        savedTime > latestSubmissionTime
          ? autosavedCode
          : latestSubmissions?.[0];
    }

    if (!latestObj) {
      latestObj = validAutoSave ? autosavedCode : latestSubmissions?.[0];
    }
    return latestObj;
  };

  useEffect(() => {
    let updated = false;

    if (PreviousSubmissions) {
      const temp = PreviousSubmissions?.getAssignmentSubmit?.autosave?.filter(
        (ele) => question?.id && ele.question_id === question?.id
      )[0];

      const scopedAutosave = temp?.answer && temp?.language ? temp : null;

      if (scopedAutosave) {
        setAutosavedCode(scopedAutosave);
        updated = true;

        if (data?.getQuestion?.showFunctionOnly) {
          const functionCode = extractFunctionCode(
            scopedAutosave.answer,
            language,
            data.getAssignmentQuestionForStudents.targetFunctionName
          );
          setCode(functionCode);
          setCode(functionCode);
        } else {
          setCode(scopedAutosave.answer);
          setInitialCode(scopedAutosave.answer);
        }
        setLanguage(scopedAutosave.language);
      }
    }
    const defaultLanguage = Assignment?.getAssignment?.languages?.[0];

    if (!updated && defaultLanguage) {
      setLanguage(defaultLanguage?.toUpperCase());
      setDefaultCodeBasedOnLanguage(defaultLanguage?.toUpperCase());
    }
  }, [Assignment, PreviousSubmissions, question]);

  useEffect(() => {
    setDefaultCodeBasedOnLanguage(language);
  }, [language, Assignment]);

  useEffect(() => {
    if (data?.getAssignmentQuestionForStudents) {
      setQuestion(data?.getAssignmentQuestionForStudents);
    }
  }, [data]);

  useEffect(() => {
    // Helper function to infer the reason for tab switch based on context
    const inferTabSwitchReason = (detectionData) => {
      const { recentKeypress, mouseX, mouseY, innerHeight, innerWidth } =
        detectionData;
      const currentTime = Date.now();
      const keyPressRecent =
        recentKeypress && currentTime - parseInt(recentKeypress) < 1000;

      if (keyPressRecent) {
        return "Keyboard shortcut detected - possibly Alt+Tab or Windows key";
      }

      const mouseYPos = parseInt(mouseY);
      const mouseXPos = parseInt(mouseX);

      if (mouseYPos < 100) {
        if (mouseXPos > innerWidth - 200) {
          return "Clicked near window controls - close/minimize button area";
        }
        return "Clicked near top of screen - browser tab or address bar area";
      }

      if (mouseYPos > innerHeight - 100) {
        return "Clicked near bottom of screen - taskbar or notification area";
      }

      return "Focus lost - switched to another window or application";
    };

    const handleTabSwitch = () => {
      // Skip counting if a system modal (like 58-min warning) is open
      try {
        if (sessionStorage.getItem("bt_skip_tab_switch") === "true") {
          return;
        }
      } catch {}

      // Collect context data
      const detectionData = {
        timestamp: new Date().toISOString(),
        visibilityState: document.visibilityState,
        hasFocus: document.hasFocus(),
        activeElement: document.activeElement?.tagName,
        screenX: window.screenX,
        screenY: window.screenY,
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        recentKeypress: sessionStorage.getItem("lastKeyTime"),
        mouseX: sessionStorage.getItem("lastMouseX") || "0",
        mouseY: sessionStorage.getItem("lastMouseY") || "0",
      };

      // Infer reason
      const reason = inferTabSwitchReason(detectionData);
      setTabSwitchReason(reason);

      console.log("Tab switch detected:", { detectionData, reason });

      setTabSwitchCount((prev) => {
        const newCount = prev + 1;
        try {
          const sid = student?.getStudentByEmailId?.id;
          if (sid && assignmentId) {
            sessionStorage.setItem(
              `tabSwitchCount_${sid}_${assignmentId}`,
              newCount
            );
          }
        } catch (error) {
          console.error("Error storing tab switch count:", error);
        }

        if (newCount >= tabSwitchLimit && !autoSubmittedRef.current) {
          // ✅ Use scoped key and append instead of overwrite
          const scopedKey = localStorage.getItem('current_cheat_key') || "cheat_reasons_string";
          const timestamp = new Date().toLocaleString();
          const previous = localStorage.getItem(scopedKey) || "";
          localStorage.setItem(scopedKey, previous + `${reason} | ${timestamp}\n`);
          autoSubmittedRef.current = true;
          if (
            submitRef.current &&
            typeof submitRef.current.forceSubmit === "function"
          ) {
            submitRef.current.forceSubmit("tab");
          } else {
            // Retry shortly in case TestLayout hasn't set submitRef yet
            setTimeout(() => {
              if (
                submitRef.current &&
                typeof submitRef.current.forceSubmit === "function"
              ) {
                submitRef.current.forceSubmit("tab");
              }
            }, 100);
          }
        } else if (newCount > 0 && newCount < tabSwitchLimit) {
          // Counts 1 and 2: Show warning modal
          // Tab switch has PRIORITY - close proctoring popup if open
          try {
            if (sessionStorage.getItem("bt_proctoring_popup_open") === "true") {
              // Close any open proctoring popup by closing all swal modals
              if (typeof window.swal !== 'undefined') {
                window.swal.close();
              }
              sessionStorage.removeItem("bt_proctoring_popup_open");
            }
          } catch {}
          sessionStorage.setItem("bt_skip_tab_switch", "true");
          setShowTabWarningModal(true);
        }
        return newCount;
      });
    };

    // Track keyboard activity
    const trackKeyboard = (e) => {
      sessionStorage.setItem("lastKeyTime", Date.now().toString());
      sessionStorage.setItem("lastKey", e.key);
    };

    // Track mouse position (throttled)
    let mouseThrottle;
    const trackMouse = (e) => {
      if (mouseThrottle) return;
      mouseThrottle = setTimeout(() => {
        sessionStorage.setItem("lastMouseX", e.clientX.toString());
        sessionStorage.setItem("lastMouseY", e.clientY.toString());
        mouseThrottle = null;
      }, 100);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        handleTabSwitch();
      }
    };
    const onWindowBlur = () => {
      handleTabSwitch();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("blur", onWindowBlur);
    document.addEventListener("keydown", trackKeyboard);
    document.addEventListener("mousemove", trackMouse);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("blur", onWindowBlur);
      document.removeEventListener("keydown", trackKeyboard);
      document.removeEventListener("mousemove", trackMouse);
    };
  }, [student?.getStudentByEmailId?.id, assignmentId, tabSwitchLimit]);

  // ✅ Wake Lock: Prevent screen sleep during exam
  useEffect(() => {
    let wakeLock = null;

    const requestWakeLock = async () => {
      try {
        if ("wakeLock" in navigator) {
          wakeLock = await navigator.wakeLock.request("screen");
          wakeLockRef.current = wakeLock;
          console.log("✅ Screen sleep prevention enabled");

          wakeLock.addEventListener("release", () => {
            console.log("Wake lock released");
          });
        } else {
          console.warn("⚠️ Wake Lock API not supported in this browser");
        }
      } catch (err) {
        console.error("Failed to enable wake lock:", err.message);
      }
    };

    const handleVisibilityChange = async () => {
      if (wakeLock !== null && document.visibilityState === "visible") {
        // Re-acquire wake lock when tab becomes visible again
        try {
          wakeLock = await navigator.wakeLock.request("screen");
          wakeLockRef.current = wakeLock;
          console.log("✅ Wake lock re-acquired");
        } catch (err) {
          console.error("Failed to re-acquire wake lock:", err);
        }
      }
    };

    // Enable wake lock when component mounts
    requestWakeLock();

    // Re-acquire wake lock when page becomes visible
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup: Release wake lock when component unmounts
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      if (wakeLockRef.current !== null) {
        wakeLockRef.current
          .release()
          .then(() => {
            console.log("🔓 Wake lock released on cleanup");
            wakeLockRef.current = null;
          })
          .catch((err) => {
            console.error("Error releasing wake lock:", err);
          });
      }
    };
  }, []); // Run once on mount

  const handleFullScreenChange = () => {
    const isCurrentlyFullscreen = !!document.fullscreenElement;

    // If user exited fullscreen without permission (not intentional)
    if (
      !isCurrentlyFullscreen &&
      isFullScreen &&
      !showExitFullscreenModal &&
      !intentionalExitRef.current
    ) {
      // Show confirmation modal
      setShowExitFullscreenModal(true);
      // Immediately try to re-enter fullscreen
      setTimeout(() => {
        if (!document.fullscreenElement) {
          document.documentElement
            .requestFullscreen({ navigationUI: "hide" })
            .then(() => {
              enableKeyboardLock();
            })
            .catch((err) => console.error("Re-enter fullscreen failed:", err));
        }
      }, 100);
    }

    // Reset intentional exit flag after exit completes
    if (!isCurrentlyFullscreen && intentionalExitRef.current) {
      intentionalExitRef.current = false;
    }

    setFullScreen(isCurrentlyFullscreen);
  };

  const enableKeyboardLock = async () => {
    try {
      // Lock keyboard to prevent Esc, F11, etc.
      if ("keyboard" in navigator && "lock" in navigator.keyboard) {
        await navigator.keyboard.lock([
          "Escape",
          "F11",
          "AltLeft",
          "AltRight",
          "MetaLeft",
          "MetaRight", // Windows key
          "Tab",
        ]);
        keyboardLockActive.current = true;
        console.log("✅ Keyboard locked - Esc and F11 blocked");
        console.warn(
          "⚠️ NOTE: Windows key and Alt+Tab CANNOT be blocked by browsers (OS-level security). Tab switch detection will catch these instead."
        );
      }
    } catch (err) {
      console.warn("Keyboard lock not supported or failed:", err);
      console.info(
        "Browser may not support Keyboard Lock API. Using tab switch detection instead."
      );
    }
  };

  const disableKeyboardLock = () => {
    try {
      if ("keyboard" in navigator && "unlock" in navigator.keyboard) {
        navigator.keyboard.unlock();
        keyboardLockActive.current = false;
        console.log("🔓 Keyboard unlocked");
      }
    } catch (err) {
      console.warn("Keyboard unlock failed:", err);
    }
  };

  const toggleFullScreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement
        .requestFullscreen({ navigationUI: "hide" })
        .then(() => {
          enableKeyboardLock();
        })
        .catch((err) => console.error("Fullscreen request failed:", err));
    }
  };

  // Removed: No longer allowing intentional exit during assessment

  const handleCancelExitFullscreen = () => {
    setShowExitFullscreenModal(false);
    // Force back to fullscreen
    if (!document.fullscreenElement) {
      document.documentElement
        .requestFullscreen({ navigationUI: "hide" })
        .then(() => {
          enableKeyboardLock();
        })
        .catch((err) => console.error("Re-enter fullscreen failed:", err));
    }
  };

  useEffect(() => {
    document.addEventListener("fullscreenchange", handleFullScreenChange);

    // Auto-trigger fullscreen when page loads (after user interaction)
    const autoEnterFullscreen = () => {
      if (!document.fullscreenElement && !loading) {
        document.documentElement
          .requestFullscreen({ navigationUI: "hide" })
          .then(() => {
            enableKeyboardLock();
            console.log("✅ Auto-entered fullscreen mode");
          })
          .catch((err) => {
            console.warn(
              "Auto-fullscreen failed (user interaction may be needed):",
              err
            );
          });
      }
    };

    // Try to enter fullscreen after a short delay (after page is interactive)
    const timer = setTimeout(autoEnterFullscreen, 500);

    // Enable keyboard lock if already in fullscreen
    if (document.fullscreenElement) {
      enableKeyboardLock();
    }

    return () => {
      clearTimeout(timer);
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
      disableKeyboardLock();
    };
  }, [loading]);

  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [testResults]);

  useEffect(() => {
    if (error) {
      errorRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [error]);

  useEffect(() => {
    if (
      compileData &&
      compileData?.compileCode?.compilationOutput?.stderr === ""
    ) {
      setTestResults(compileData.compileCode.Results);
    } else {
      setTestResults([]);
    }
  }, [compileData]);

  useEffect(() => {
    if (submitData?.submitAssignmentCode) {
      setOutput(submitData.submitAssignmentCode);
    }
  }, [submitData]);

  const handleCodeChange = (newValue) => {
    setCode(newValue);
  };

  useEffect(() => {
    setIsCodeChanged(code !== initialCode);
  }, [code, initialCode]);

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
    setRun(false);
    setInput("");
  };
  const assignmentSubmitInput = {
    assignment_id: assignmentId,
    question_id: question.id,
    student_id: student?.getStudentByEmailId.id,
    cluster_id: student?.getStudentByEmailId.clusterIds[0],
    campus_name: campus?.campus?.name,
  };

  const handleSubmit = async () => {
    try {
      await submitCode({
        variables: {
          assignmentCompilerCodeInput: {
            code,
            language,
            questionid: question.id,
            testCases: question?.test_cases?.map((test) => ({
              id: test.id,
              input: test.input,
              output: test.output,
              visibility: test.visibility,
              weightage: parseFloat(test.weightage),
            })),
          },
          assignmentSubmitInput: assignmentSubmitInput,
        },
      });
      setSubmit(true);
      setRun(false);
      setInput("");
    } catch (error) {
      console.error(error);
    }
  };

  const handleRun = async () => {
    try {
      setSubmit(false);
      setError(false);

      let data;

      if (!isChecked) {
        const response = await compileCode({
          variables: {
            compileCodeInput: {
              code,
              testCases: question?.test_cases?.map((test) => ({
                id: test.id,
                input: test.input,
                output: test.output,
                visibility: test.visibility,
                weightage: parseFloat(test.weightage),
              })),
              language,
              questionid: question.id,
            },
          },
        });
        setRun(true);
        data = response.data;
      } else {
        const response = await compileCode({
          variables: {
            compileCodeInput: {
              code,
              testCases: [
                {
                  id: "0",
                  input: input,
                  output: " ",
                  visibility: "SAMPLE",
                  weightage: 0,
                },
              ],
              language,
              questionid: question.id,
            },
          },
        });
        data = response.data;
      }

      if (data && data?.compileCode?.compilationOutput?.stderr === "") {
        setRun(true);
        setTestResults(data.compileCode.Results);
      }
    } catch (err) {
      setTestResults([]);

      if (err.graphQLErrors && err.graphQLErrors.length > 0) {
        const errorMessage = err.graphQLErrors[0].message;
        setError(errorMessage);
      } else {
        setError(
          "There could some logic error in your code or network connectivity issue. Kindly try again later"
        );
      }
    }
  };

  const setToDefault = () => {
    const langKey = language?.toLowerCase();
    if (
      data?.getAssignmentQuestionForStudents.showFunctionOnly &&
      data?.getAssignmentQuestionForStudents.predefinedCode[langKey]
    ) {
      const targetFunctionCode = extractFunctionCode(
        data.getAssignmentQuestionForStudents.predefinedCode[langKey],
        langKey,
        data.getAssignmentQuestionForStudents.targetFunctionName
      );
      setCode(targetFunctionCode);
      setInitialCode(targetFunctionCode);
    } else if (data?.getAssignmentQuestionForStudents.predefinedCode[langKey]) {
      setCode(data.getAssignmentQuestionForStudents.predefinedCode[langKey]);
      setInitialCode(
        data.getAssignmentQuestionForStudents.predefinedCode[langKey]
      );
    }
  };

  const remainingTimeInMs =
    PreviousSubmissions?.getAssignmentSubmit?.timeRemaining ?? 0;
  const dueDateMs = Assignment?.getAssignment?.due_date
    ? new Date(Assignment.getAssignment.due_date).getTime()
    : null;
  const dueRemainingTimeInMs = dueDateMs !== null ? Math.max(0, dueDateMs - Date.now()) : null;
  const effectiveRemainingTimeInMs = dueRemainingTimeInMs !== null
    ? Math.min(remainingTimeInMs, dueRemainingTimeInMs)
    : remainingTimeInMs;
  const remainingDurationInHours = effectiveRemainingTimeInMs / (1000 * 60 * 60);

  const handleTimeUp = () => {
    if (
      submitRef.current &&
      typeof submitRef.current.forceSubmit === "function"
    ) {
      submitRef.current.forceSubmit("time");
      return;
    }

    setTimeout(() => {
      if (
        submitRef.current &&
        typeof submitRef.current.forceSubmit === "function"
      ) {
        submitRef.current.forceSubmit("time");
      }
    }, 100);
  };

  useEffect(() => {
    if (!PreviousSubmissions?.getAssignmentSubmit) return;
    if (PreviousSubmissions?.getAssignmentSubmit?.completed) return;

    if (effectiveRemainingTimeInMs > 0) {
      timeAutoSubmitTriggeredRef.current = false;
      return;
    }

    if (timeAutoSubmitTriggeredRef.current) return;
    timeAutoSubmitTriggeredRef.current = true;
    handleTimeUp();
  }, [effectiveRemainingTimeInMs, PreviousSubmissions]);

  const handleCloseTabWarningModal = () => {
    setShowTabWarningModal(false);
    // Remove the skip flag after a short delay to allow modal to fully close
    setTimeout(() => {
      try {
        sessionStorage.removeItem("bt_skip_tab_switch");
      } catch {}
    }, 300);
  };

  // Auto-close the Tab Switch Warning Modal after 5 seconds if not closed manually
  useEffect(() => {
    if (!showTabWarningModal) return;
    const timer = setTimeout(() => {
      // If still open after 5s, close it programmatically
      if (showTabWarningModal) {
        try {
          handleCloseTabWarningModal();
        } catch {}
      }
    }, 10000);
    return () => clearTimeout(timer);
  }, [showTabWarningModal]);

  return (
    <>
      {/* Exit Fullscreen Confirmation Modal */}
      <Modal
        show={showExitFullscreenModal}
        onClose={handleCancelExitFullscreen}
        size="md"
        popup
        dismissible={false}
      >
        <Modal.Body>
          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 text-3xl text-red-600 bg-red-100 rounded-full">
              ⚠️
            </div>
            <h3 className="mb-5 text-lg font-semibold text-gray-900 dark:text-gray-400">
              Cannot Exit Fullscreen During Assessment
            </h3>
            <p className="mb-5 text-base text-gray-700 dark:text-gray-400">
              You must remain in fullscreen mode to continue the assessment.
              Exiting fullscreen is not allowed until you complete the test.
            </p>
            <div className="flex justify-center">
              <Button className="text-white bg-[#0e3d5d] hover:bg-[#0e3d5d]" onClick={handleCancelExitFullscreen}>
                Back to Fullscreen
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Tab Switch Warning Modal */}
      <Modal
        show={showTabWarningModal}
        onClose={handleCloseTabWarningModal}
        size="md"
        popup
        dismissible={false}
      >
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 text-3xl text-yellow-600 bg-yellow-100 rounded-full">
              ⚠️
            </div>
            <h3 className="mb-5 text-lg font-semibold text-gray-900 dark:text-gray-400">
              Tab Switch/Popup Detected
            </h3>

            {/* Detected reason */}
            {tabSwitchReason && (
              <div className="px-4 py-3 mb-4 text-sm text-center bg-gray-100 border-yellow-500 rounded dark:bg-gray-700">
                <p className="mt-1 text-gray-600 dark:text-gray-400">
                  {tabSwitchReason}
                </p>
              </div>
            )}

            <p className="mb-5 text-base text-gray-700 dark:text-gray-400">
              Switching tabs or opening other popups again will{" "}
              <strong>auto-submit</strong> your test.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Do not leave this page.
            </p>
          </div>
        </Modal.Body>
      </Modal>

      <TestLayout
        assignmentId={assignmentId}
        disableSubmit={true}
        submitRef={submitRef}
      >
        {/* Tab switch alert removed: now auto-submits immediately when threshold reached */}
        {/* Shimmer UI while loading question */}
        {loading ? (
          <div className="flex-grow min-h-screen bg-slate-100 md:overflow-hidden font-poppins">
            <div className="flex items-center justify-between px-8 pt-8 mb-6">
              <div className="w-32 h-10 bg-gray-200 rounded animate-pulse" />
              <div className="w-32 h-10 bg-gray-200 rounded animate-pulse" />
            </div>
            <div className="flex max-md:flex-col h-[calc(100%-48px)] px-8">
              <div
                className="pr-4 overflow-y-auto md:w-1/2"
                style={{ maxHeight: "100%" }}
              >
                <div className="p-6 mb-4 bg-white border border-gray-200 rounded-lg shadow-lg">
                  <div className="w-3/4 h-6 mb-4 bg-gray-200 rounded animate-pulse" />
                  <div className="w-1/2 h-4 mb-2 bg-gray-200 rounded animate-pulse" />
                  <div className="w-full h-4 mb-2 bg-gray-200 rounded animate-pulse" />
                  <div className="w-2/3 h-4 mb-2 bg-gray-200 rounded animate-pulse" />
                </div>
              </div>
              <div
                className="pl-4 overflow-y-auto md:w-1/2"
                style={{ maxHeight: "100%" }}
              >
                <div className="p-6 mb-4 bg-white border border-gray-200 rounded-lg shadow-lg">
                  <div className="w-1/2 h-6 mb-4 bg-gray-200 rounded animate-pulse" />
                  <div className="w-full h-4 mb-2 bg-gray-200 rounded animate-pulse" />
                  <div className="w-2/3 h-4 mb-2 bg-gray-200 rounded animate-pulse" />
                  <div className="w-1/2 h-4 mb-2 bg-gray-200 rounded animate-pulse" />
                </div>
              </div>
            </div>
          </div>
        ) : isFullScreen ? (
          <div className="flex-grow min-h-screen bg-white md:overflow-hidden font-poppins">
            <div className="flex items-center justify-between pt-8 pl-8 pr-4 mb-6">
              <BackButton />
              <Timer
                durationInHours={remainingDurationInHours}
                onTimeUp={handleTimeUp}
              />
            </div>
            <div className="flex max-md:flex-col h-[calc(100%-48px)] px-8">
              <div
                className="pr-4 overflow-y-auto md:w-1/2"
                style={{ maxHeight: "100%" }}
              >
                {/* Background container for question card with shadow */}
                <div className="p-6 mb-4 bg-white border border-gray-200 rounded-lg shadow-lg">
                  <CompilerQuestionCard
                    question={question}
                    loading={loading}
                    onApply={setCode}
                  />
                </div>
              </div>

              <div
                className="pl-4 overflow-y-auto md:w-1/2"
                style={{ maxHeight: "100%" }}
              >
                {/* Background container for editor, buttons, and above box with enhanced shadow */}
                <div className="p-6 mb-4 bg-white border border-gray-200 rounded-lg shadow-lg">
                  <Compiler
                    student_id={student?.getStudentByEmailId?.id}
                    question_id={questionId}
                    language={language}
                    code={code}
                    setCodeApplied={setCodeApplied}
                    setToDefault={setToDefault}
                    setLanguage={setLanguage}
                    handleCodeChange={handleCodeChange}
                    errorRef={errorRef}
                    handleSubmit={handleSubmit}
                    handleRun={handleRun}
                    CourseData={Assignment?.getAssignment}
                    compileLoading={compileLoading}
                    submitLoading={submitLoading}
                    submitInput={assignmentSubmitInput}
                    isAssignment
                    isChecked={isChecked}
                    isCodeChanged={isCodeChanged}
                    setInitialCode={setInitialCode}
                    manualInput={input}
                  />
                  <CustomInput
                    isChecked={isChecked}
                    handleCheckboxChange={handleCheckboxChange}
                    setInput={setInput}
                    input={input}
                  />
                </div>

                {!compileerror && run && testResults?.length !== 0 && (
                  <RunModel
                    tableRef={tableRef}
                    isChecked={isChecked}
                    testResults={testResults}
                    loading={compileLoading}
                  />
                )}

                {output && submit && (
                  <SumbitModel output={output} question={question} />
                )}

                {compileerror && !submit && (
                  <ErrorModel error={compileerror} language={language} />
                )}

                {testResults.length === 0 &&
                  !submit &&
                  run &&
                  !compileLoading &&
                  !compileerror && (
                    <ErrorModel
                      error={
                        compileData?.compileCode?.compilationOutput?.stderr
                      }
                      language={language}
                      runtimeError={testResults.length === 0}
                    />
                  )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center w-screen h-screen bg-gray-100">
            <h1 className="mb-4 text-xl">
              Please switch to Fullscreen to continue
            </h1>
            <Button className="p-0 bg-primary" onClick={toggleFullScreen}>
              Full Screen
            </Button>
          </div>
        )}
      </TestLayout>
    </>
  );
};

export default AssignmentTestQuestion;